import { useMutation } from '@apollo/client';
import { Stack } from '@mui/material';
import { Text } from '@telegram-apps/telegram-ui';
import { useCallback, useEffect, useMemo, useRef, useState, type FC } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import { ButtonDock } from '@focuslee/shared-mini-app';
import { CreateRewardMutation, CreateRewardMutationVariables, RewardRarity, UpdateRewardMutation, UpdateRewardMutationVariables } from '../client/generated/graphql';
import { createRewardCache, createRewardMutation } from '../client/mutations/createReward';
import { updateRewardMutation } from '../client/mutations/updateReward';
import { Page } from '../components/Page';
import { CommonIcon } from '../components/common/CommonIcon';
import { CustomInput } from '../components/common/CustomInput';
import { CustomSelect } from '../components/common/CustomSelect';
import { IconPicker } from '../components/common/IconPicker';
import { IconPickerSettings } from '../components/common/IconPickerSettings';
import { AnalyticsEventType } from '../contexts/analyticsEvents';
import { getRandomIcon } from '../helpers/common';
import { getRewardColor, getRewardColorCode } from '../helpers/reward';
import { getButtonThemeColors } from '../helpers/theme';
import { useCachedReward } from '../hooks/caching/reward';
import { useAnalytics } from '../hooks/useAnalytics';

export const RewardCreateEditPage: FC = () => {
  const inputRef = useRef<HTMLInputElement>(null);
  const hasAutoFocusedRef = useRef(false);
  const { id: rewardId } = useParams<{ id?: string }>();
  const editingReward = useCachedReward(rewardId);
  
  const [createReward, { loading: isCreateLoading }] = useMutation<CreateRewardMutation, CreateRewardMutationVariables>(createRewardMutation, {
      update: createRewardCache
  });
  const [updateReward, { loading: isUpdateLoading }] = useMutation<UpdateRewardMutation, UpdateRewardMutationVariables>(updateRewardMutation);
  
  const [rewardName, setRewardName] = useState<string>('');
  const [description, setDescription] = useState<string>('');
  const [price, setPrice] = useState<string>('');
  const [rarity, setRarity] = useState<RewardRarity>(RewardRarity.Uncommon);
  const [icon, setIcon] = useState<string>(getRandomIcon());
  const [showIconSettings, setShowIconSettings] = useState(false);
  const [isInputFocused, setIsInputFocused] = useState(false);
  
  const isValid = !!rewardName && !!price && !!rarity && !!icon && !isNaN(Number(price)) && Number(price) > 0;
  const navigate = useNavigate();
  const { recordEvent } = useAnalytics();

  const rarityOptions = useMemo(() => [RewardRarity.Uncommon, RewardRarity.Rare, RewardRarity.Epic, RewardRarity.Legendary].map(r => ({
    id: r,
    label: r,
    shortLabel: r
  })), []);

  const isSaving = isCreateLoading || isUpdateLoading;

  useEffect(() => {
    if (
      inputRef.current &&
      !editingReward &&
      !showIconSettings &&
      !hasAutoFocusedRef.current
    ) {
      inputRef.current.focus();
      hasAutoFocusedRef.current = true;
    }
  }, [editingReward, showIconSettings]);

  useEffect(() => {
    if (editingReward) {
      setRewardName(editingReward.name);
      setDescription(editingReward.description);
      setPrice(editingReward.price.toString());
      setRarity(editingReward.rarity);
      setIcon(editingReward.icon || getRandomIcon());
    }
  }, [editingReward]);

  const navigateBack = useCallback(() => {
    navigate(-1);
  }, []);

  const handleInputFocus = useCallback(() => {
    setIsInputFocused(true);
  }, []);

  const handleInputBlur = useCallback(() => {
    requestAnimationFrame(() => {
      const activeElement = document.activeElement as HTMLElement | null;
      if (!activeElement || activeElement.tagName !== 'INPUT') {
        setIsInputFocused(false);
      }
    });
  }, []);

  const handleIconClick = useCallback(() => {
    inputRef.current?.blur();
    setIsInputFocused(false);
    setShowIconSettings(true);
  }, []);

  const handleSaveIconSettings = useCallback(async (settings: { icon: string }) => {
    setIcon(settings.icon);
    setShowIconSettings(false);
    setIsInputFocused(false);
  }, []);

  const handleCancelIconSettings = useCallback(() => {
    setShowIconSettings(false);
    setIsInputFocused(false);
  }, []);

  const handleCreate = useCallback(async () => {
    if (!isValid || isCreateLoading || isUpdateLoading) return;

    const priceNumber = parseInt(price);
    const commonVariables = {
      name: rewardName!,
      description: description ?? '',
      price: priceNumber,
      rarity: rarity,
      icon,
    };

    try {
      if (rewardId) {
        await updateReward({ variables: { rewardId, ...commonVariables } });
        recordEvent(AnalyticsEventType.REWARD_UPDATED, { rewardId });
      } else {
        const result = await createReward({ variables: { ...commonVariables } });
        recordEvent(AnalyticsEventType.REWARD_CREATED, { rewardId: result.data?.createReward?.id });
      }
      navigateBack();
    } catch (error) {
        console.error("Error creating or updating reward:", error);
    }
  }, [isValid, isCreateLoading, isUpdateLoading, rewardName, description, price, rarity, icon, rewardId, updateReward, createReward]);

  if (showIconSettings) {
    return (
      <IconPickerSettings
        initialIcon={icon}
        onSave={handleSaveIconSettings}
        onCancel={handleCancelIconSettings}
      />
    );
  }
  
  return (
    <Page
      back={true}
      insetEnabled={false}
      footer={
        <ButtonDock
          secondary={{ text: 'Back', onClick: navigateBack, navigation: 'back' }}
          main={{
            text: rewardId ? 'Save' : 'Create',
            onClick: handleCreate,
            enabled: isValid && !isSaving,
            loading: isSaving,
            bgColor: getButtonThemeColors(isValid && !isSaving).backgroundColor,
          }}
        />
      }
    >
      <Stack alignItems="center" justifyContent="center" minHeight="var(--tg-viewport-content-safe-area-inset-top)">
        <Text>{ rewardId ? 'Edit Reward' : 'Create Reward' }</Text>
      </Stack>
      <Stack 
        height="100%" 
        paddingLeft="10px" 
        paddingRight="10px" 
        paddingTop="4px" 
        gap={2}
        sx={{
          overflow: isInputFocused ? 'hidden' : 'visible',
          maxHeight: isInputFocused ? '70vh' : 'none'
        }}
      >
        {!isInputFocused && icon && rarity && (
          <Stack alignSelf="center" sx={{ py: 2 }}>
            <Stack 
              sx={{
                width: 80,
                height: 80,
                borderRadius: 2,
                background: getRewardColor(rarity),
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
            >
              <CommonIcon
                icon={icon}
                type="filled"
                size={40}
                color="var(--tg-theme-text-color)"
              />
            </Stack>
          </Stack>
        )}
        <CustomInput
          value={rewardName} 
          onChange={(e) => setRewardName(e.target.value)}
          onFocus={handleInputFocus}
          onBlur={handleInputBlur}
          ref={inputRef} 
          style={{ fontSize: '20px', lineHeight: '20px' }} 
          placeholder="Reward Name" 
          wrapperStyle={{ minHeight: '0px' }}
        />
        <CustomInput 
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          onFocus={handleInputFocus}
          onBlur={handleInputBlur}
          placeholder="Description (optional)" 
          wrapperStyle={{ minHeight: '0px' }}
        />
        <Stack direction="row" gap={1} justifyContent="space-between" alignItems="center">
            <Stack direction="row" gap={1} alignItems="center">
                <CommonIcon icon="paid" type="filled" color="var(--tg-theme-text-color)" size={20} />
                <CustomInput
                    value={price}
                    disabled={!!editingReward && editingReward.isRewarded}
                    onChange={(e) => setPrice(e.target.value)}
                    onFocus={handleInputFocus}
                    onBlur={handleInputBlur}
                    placeholder="Price" 
                    type="number"
                    wrapperStyle={{ minHeight: '0px' }}
                />
            </Stack>
            <CustomSelect 
                selectedId={rarity}
                setSelectedId={(id) => setRarity(id as RewardRarity)}
                options={rarityOptions}
                text={(r) => r.label}
                renderSelected={(r) => r ? r.shortLabel : 'Rarity'}
                renderSelectedBefore={() => <CommonIcon icon="star" type="filled" color="var(--tg-theme-text-color)" size={20} />}
            />
        </Stack>
        
        <IconPicker
          selectedIcon={icon}
          selectedIconColor={getRewardColorCode(rarity)}
          onClick={handleIconClick}
        />
      </Stack>
    </Page>
  );
};
